const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function fetchAPI(url: string, options: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export interface LearningGroup {
  id: number;
  name: string;
  description?: string;
  courseId?: number;
  courseTitle?: string;
  createdBy: number;
  maxMembers?: number;
  isPublic: boolean;
  memberCount: number;
  isFull: boolean;
  createdAt: string;
  isMember?: boolean;
  /** User is a group-admin MEMBER of this group (#412) */
  isAdmin?: boolean;
  /** User may manage the group: group admin, own-company admin, platform roles (#412) */
  canManage?: boolean;
  messageCount?: number;
}

export interface LearningGroupMember {
  id: number;
  userId: number;
  userName?: string;
  userEmail?: string;
  role: 'admin' | 'member';
  joinedAt: string;
}

export interface LearningGroupMessage {
  id: number;
  userId: number;
  userName: string;
  content: string;
  createdAt: string;
}

export interface CreateGroupData {
  name: string;
  description?: string;
  courseId?: number;
  isPublic?: boolean;
  maxMembers?: number;
}

class LearningGroupService {
  /**
   * Get all available public groups
   */
  async getGroups(): Promise<LearningGroup[]> {
    return fetchAPI(`${API_BASE_URL}/api/learning-groups`);
  }

  /**
   * Get groups where current user is a member
   */
  async getMyGroups(): Promise<LearningGroup[]> {
    return fetchAPI(`${API_BASE_URL}/api/learning-groups/my`);
  }

  /**
   * Create a new learning group
   */
  async createGroup(data: CreateGroupData): Promise<LearningGroup> {
    return fetchAPI(`${API_BASE_URL}/api/learning-groups`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get group details
   */
  async getGroup(id: number): Promise<LearningGroup> {
    return fetchAPI(`${API_BASE_URL}/api/learning-groups/${id}`);
  }

  /**
   * Update a group
   */
  async updateGroup(id: number, data: Partial<CreateGroupData>): Promise<LearningGroup> {
    return fetchAPI(`${API_BASE_URL}/api/learning-groups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a group
   */
  async deleteGroup(id: number): Promise<void> {
    await fetchAPI(`${API_BASE_URL}/api/learning-groups/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Join a group
   */
  async joinGroup(id: number): Promise<{ success: boolean; message: string }> {
    return fetchAPI(`${API_BASE_URL}/api/learning-groups/${id}/join`, {
      method: 'POST',
    });
  }

  /**
   * Leave a group
   */
  async leaveGroup(id: number): Promise<{ success: boolean; message: string }> {
    return fetchAPI(`${API_BASE_URL}/api/learning-groups/${id}/leave`, {
      method: 'POST',
    });
  }

  /**
   * Get group members
   */
  async getMembers(groupId: number): Promise<LearningGroupMember[]> {
    return fetchAPI(`${API_BASE_URL}/api/learning-groups/${groupId}/members`);
  }

  /**
   * Get group messages
   */
  async getMessages(groupId: number, limit = 50, offset = 0): Promise<LearningGroupMessage[]> {
    return fetchAPI(`${API_BASE_URL}/api/learning-groups/${groupId}/messages?limit=${limit}&offset=${offset}`);
  }

  /**
   * Post a message to the group.
   * The sender's display name is resolved server-side (#402) — the client
   * only sends the message text.
   */
  async postMessage(groupId: number, content: string): Promise<LearningGroupMessage> {
    return fetchAPI(`${API_BASE_URL}/api/learning-groups/${groupId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  /**
   * Add a user to the group (admin only)
   */
  async addMember(groupId: number, userId: number): Promise<{ success: boolean; message: string; member: LearningGroupMember }> {
    return fetchAPI(`${API_BASE_URL}/api/learning-groups/${groupId}/add-member`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  /**
   * Promote a member to admin
   */
  async promoteMember(groupId: number, memberId: number): Promise<{ success: boolean; message: string }> {
    return fetchAPI(`${API_BASE_URL}/api/learning-groups/${groupId}/members/${memberId}/promote`, {
      method: 'POST',
    });
  }

  /**
   * Remove a member from the group
   */
  async removeMember(groupId: number, memberId: number): Promise<{ success: boolean; message: string }> {
    return fetchAPI(`${API_BASE_URL}/api/learning-groups/${groupId}/members/${memberId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Create SSE connection for real-time messages
   * Returns an EventSource instance and cleanup function
   */
  createMessageStream(
    groupId: number,
    lastMessageId: number,
    onMessage: (message: LearningGroupMessage) => void,
    onError?: (error: Event) => void,
    onReconnect?: () => void
  ): { eventSource: EventSource; cleanup: () => void } {
    const token = localStorage.getItem('token');
    const url = `${API_BASE_URL}/api/learning-groups/${groupId}/stream`;

    const eventSource = new EventSource(url, {
      // Note: EventSource doesn't support custom headers directly
      // We'll need to pass the token via query param or use a polyfill
    });

    // For now, fall back to efficient polling since EventSource doesn't support auth headers
    // We'll implement a custom solution
    let isActive = true;
    let currentLastId = lastMessageId;
    const pollInterval = 2000; // 2 seconds when active

    const poll = async () => {
      if (!isActive) return;

      try {
        const messages = await this.getMessagesSince(groupId, currentLastId);
        messages.forEach((msg) => {
          onMessage(msg);
          currentLastId = Math.max(currentLastId, msg.id);
        });
      } catch (error) {
        onError?.(error as Event);
      }

      if (isActive) {
        setTimeout(poll, pollInterval);
      }
    };

    // Start polling
    poll();

    return {
      eventSource,
      cleanup: () => {
        isActive = false;
        eventSource.close();
      },
    };
  }

  /**
   * Get messages since a specific message ID
   */
  async getMessagesSince(groupId: number, afterId: number): Promise<LearningGroupMessage[]> {
    return fetchAPI(`${API_BASE_URL}/api/learning-groups/${groupId}/messages/since/${afterId}`);
  }
}

export const learningGroupService = new LearningGroupService();
export default learningGroupService;
